import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const VIEWPORT_PADDING = 12;
const TOOLTIP_GAP = 10;
const MAX_TOOLTIP_WIDTH = 320;

/**
 * Observation tooltip rendered in a portal so it is not clipped by the
 * plan table cell / scroll container, and not limited to the cell width.
 */
const ShiftObservationTooltip = ({ comments }) => {
	const anchorRef = useRef(null);
	const tooltipRef = useRef(null);
	const [coords, setCoords] = useState(null);

	useLayoutEffect(() => {
		const updatePosition = () => {
			const anchor = anchorRef.current;
			const parent = anchor?.parentElement;
			if (!parent) return;

			const rect = parent.getBoundingClientRect();
			const tooltipEl = tooltipRef.current;
			const tooltipWidth = tooltipEl
				? tooltipEl.offsetWidth
				: Math.min(MAX_TOOLTIP_WIDTH, window.innerWidth - VIEWPORT_PADDING * 2);
			const tooltipHeight = tooltipEl ? tooltipEl.offsetHeight : 0;

			let left = rect.left + rect.width / 2;
			const halfWidth = tooltipWidth / 2;
			const minLeft = VIEWPORT_PADDING + halfWidth;
			const maxLeft = window.innerWidth - VIEWPORT_PADDING - halfWidth;
			left = Math.min(Math.max(left, minLeft), maxLeft);

			// Prefer above the cell; flip below if it would go off-screen.
			let top = rect.top - TOOLTIP_GAP;
			let placement = "top";
			if (tooltipHeight > 0 && top - tooltipHeight < VIEWPORT_PADDING) {
				top = rect.bottom + TOOLTIP_GAP;
				placement = "bottom";
			}

			setCoords({ left, top, placement });
		};

		updatePosition();
		// Second pass once tooltip is measured.
		const raf = requestAnimationFrame(updatePosition);

		window.addEventListener("resize", updatePosition);
		// Capture scroll from nested plan containers.
		window.addEventListener("scroll", updatePosition, true);

		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [comments]);

	const text = typeof comments === "string" ? comments.trim() : "";
	if (!text) return null;

	return (
		<>
			<span
				ref={anchorRef}
				className="shift-observation-tooltip-anchor"
				aria-hidden="true"
			/>
			{createPortal(
				<div
					ref={tooltipRef}
					className={`shift-observation-tooltip${
						coords?.placement === "bottom"
							? " shift-observation-tooltip--bottom"
							: ""
					}`}
					role="tooltip"
					style={
						coords
							? {
									left: coords.left,
									top: coords.top,
									visibility: "visible",
							  }
							: { visibility: "hidden", left: 0, top: 0 }
					}
				>
					{text}
				</div>,
				document.body
			)}
		</>
	);
};

export default ShiftObservationTooltip;
